import type { AiCapabilityPage } from "../schema.js";
import { LIVE, MANAGER_ROLES, SLOT_FUND, SLOT_GROUP, WRITE_FLAGS } from "./_shared.js";

/**
 * `COL-EQUAL` (@UC0195) — mở đợt thu đều (mỗi người một số), T2 phiếu đầy đủ. **Trang tiền đầu tiên của v2** (US-AIV2-0034).
 * Executor = `AdhocCollectionService.createAdhoc` (collections/adhoc-collection.service.ts:181; lớp `AdhocCollectionService` ⇒ executor `adhocCollection.*`) — cùng cửa với màn tạo đợt thu.
 * "Mở đợt thu" mà không nói thu kiểu gì ⇒ Người phiên dịch trả `ambiguities: [COL-EQUAL, COL-DEADLINE]` (hỏi A hay B) —
 * KHÔNG có trang cha "COL-WRITE" riêng: cơ chế kẹt-hai-trang đã nằm ở Phiếu (0012), dựng trang cha là cơ chế thứ hai.
 * Số tiền là SPAN ("hai trăm", "200k") — Người làm đổi bằng bộ đổi tiền; mơ hồ ("2 trăm") ⇒ hỏi, không đoán (§4).
 *
 * 🔴 **QĐ PO 17/09/2026 16:05 — ô `nhom` là ĐƯỜNG VÀO nhóm hiệu lực của trang tiền.** Tab trợ lý là tab TOÀN CỤC: không khai ô
 * nhóm thì `assertCanOpen` trả 403 `GROUP_REQUIRED` và trang biến khỏi menu ⇒ không ai mở được đợt thu bằng chat. Thứ tự PO chốt —
 * (a) người dùng đang chọn một nhóm ở tab AI (client gửi `groupId` kèm lượt) ⇒ nhóm đó, KHÔNG hỏi · (b) đúng 1 nhóm ⇒ lấy luôn ·
 * (c) còn lại ⇒ hỏi kèm lựa chọn — **là luật của ENGINE**, không của trang: `loadGroups` ghim nhóm hiệu lực khi phiếu đã có nhóm,
 * `autofill: "sole-active"` lo vế (b)/(c) (0023b `@UC0226`). Trang chỉ KHAI ô; khai luật lần thứ hai ở đây là cơ chế thứ hai.
 *
 * 🔴 **HẠN CHÓT: trang này KHÔNG hỏi, nhưng PHẢI NÓI.** `createAdhoc` bắt buộc `dueDate` (contract `createAdhocCollectionRequestSchema`),
 * còn "thu đều có hạn" là trang RIÊNG `COL-DEADLINE` (QĐ PO 15/09 GIỮ). Nên binding đặt hạn mặc định **cuối tháng hiện tại**.
 * Phiếu im lặng về một cái hạn sẽ ghi vào sổ = phiếu nói dối (`@UC0163`).
 * 🔴 **UX-COLLECTION-PERIOD (@UC0196, 15/10)**: câu NÓI một tháng ("thu quỹ tháng 11 mỗi người 150k") thì máy KHÔNG được quyết hạn nữa ⇒ lưới kỳ đợt thu
 * (`interpreter/collection-period.ts`) chuyển sang `COL-DEADLINE` (hạn = ngày cuối tháng đó, tên "Thu quỹ tháng 11/YYYY", ô đóng băng, thẻ in NGÀY CỤ THỂ).
 * Câu KHÔNG nói kỳ ⇒ trang này + QĐ A5 như cũ.
 *
 * 🔴🔴 **US-AIV2-0080 (QĐ PO 20/09/2026 A5) — HAI TRỤC CÂU CHỮ, ĐỪNG GỘP LẠI.**
 * `AiDoerService` so **MỌI khoá** facts giữa phiếu và lượt ghi, mà hai lượt đó cách nhau bao lâu tuỳ người dùng ⇒
 * **cái gì lên PHIẾU thì không được suy từ đồng hồ**, còn **cái gì nói SAU KHI GHI thì phải là giá trị THẬT**:
 *  · `tenDotGhi` + `hanGhi` — **hứa trước**, dùng ở `confirm`. Không-cụ-thể ("cuối tháng bạn bấm Đồng ý"), đúng ở cả
 *    hai đầu theo định nghĩa. Đo được trước bản vá: mở phiếu 30/09 23:59, bấm 01/10 00:01 ⇒ **2/4 khoá lệch** ⇒
 *    `FACTS_MISMATCH` **huỷ một đợt thu HỢP LỆ** — và không tái hiện khi chạy lại nên bị gọi là "flake".
 *  · `tenDotThat` + `hanThat` — **báo lại**, dùng ở `done`/`doneAlready`. Khoá **chỉ-có-ở-execute** (tiền lệ
 *    `conLaiThat` của `RECORD-PAID` #853) nên chúng KHÔNG vào phép so, và vì thế được phép mang tên + ngày THẬT
 *    của dòng đã ghi. Đưa chúng vào `confirm` là dựng lại đúng quả mìn vừa gỡ.
 *
 * 🔴 **Ngoài phạm vi, cố ý:** thu ĐỊNH KỲ ("mỗi tháng 500k") → trang `COL-RECURRING` (0034b, QĐ PO 17/09 09:20); chọn MỘT SỐ NGƯỜI
 * ("chỉ tổ 1 đóng") → màn GUI: resolver 0022 không có loại ô "danh sách người" (ô `list` trả chữ nguyên văn, không ra `group_members.id`),
 * nên một ô `nguoi` ở đây chỉ có thể đoán — và đoán sai thì cả nhóm nhận nghĩa vụ sai. Đợt thu của trang này áp cho MỌI suất phải đóng
 * của nhóm, và phiếu nói rõ bằng `{soNguoi}` trước khi ghi.
 */
export const COL_EQUAL = {
  id: "COL-EQUAL",
  uc: "@UC0195",
  tier: "T2",
  status: LIVE,
  roles: MANAGER_ROLES,
  flags: WRITE_FLAGS,
  slots: [
    SLOT_GROUP,
    SLOT_FUND,
    { id: "soTien", type: "money", required: true, ask: "Mỗi người đóng bao nhiêu?" },
    { id: "tenDot", type: "text", required: false, ask: "Đặt tên đợt thu là gì? (bỏ trống thì lấy theo tháng)" },
  ],
  llm: {
    description: "Mở một đợt thu mà mọi người đóng cùng một số tiền (thu đều), người mở không nói hạn chót.",
    examples: [
      "mở đợt thu 200k mỗi người",
      "mo dot thu 200k moi nguoi",
      "thu quỹ lớp hai trăm nghìn một bạn",
      "thu quy lop 200 nghin mot ban",
      "tạo đợt thu đều 500k",
      "tao dot thu deu 500k",
      "thu mỗi người 1 triệu tiền quỹ đầu năm",
      "thu 300k/người",
      "mở đợt thu",
      "thu tiền quỹ lần này 150k",
      "mọi người đóng 100k nhé",
      "thu 200k",
      // ——— 0105: thêm cho đủ 40 ———
      "mo dot thu",
      "thu tien quy lan nay 150k",
      "moi nguoi dong 100k nhe",
      "thu 300k moi nguoi",
      "mở lần thu mới, mỗi người 250k",
      "mo lan thu moi moi nguoi 250k",
      "tạo đợt thu quỹ lớp 400k một suất",
      "tao dot thu quy lop 400k mot suat",
      "cả nhóm đóng đều 50k",
      "ca nhom dong deu 50k",
      "thu tiền đội bóng mỗi bạn 120k",
      "thu tien doi bong moi ban 120k",
      "mở đợt thu đều đi",
      "mo dot thu deu di",
      "cho mình mở đợt thu 1 triệu mỗi người",
      "cho minh mo dot thu 1 trieu moi nguoi",
      "mở đợt thu kiểu gì?",
      "mo dot thu kieu gi",
      "lập đợt thu mới cho quỹ",
      "lap dot thu moi cho quy",
      // W-1 UX-COL-OPEN-PERIOD (review #1447): với hai câu dưới, LLM chọn COL-EQUAL là ĐÚNG (eval chấm trang của Phiếu, TRƯỚC mọi lưới). Câu nói KỲ
      // ("đầu năm") ⇒ lưới kỳ tất định SAU đó (`collectionPeriodPageFromSentence`) chuyển sang COL-DEADLINE HỎI hạn — không ra hạn cuối tháng NÀY.
      // Chú thích TS không vào prompt (prompt dựng từ giá trị `llm.*`) ⇒ promptHash không đổi. KHÔNG sửa chữ ví dụ (đổi ⇒ ca eval chưa đo).
      "thu quỹ đầu năm 700k một người",
      "thu quy dau nam 700k mot nguoi",
      "mỗi phụ huynh đóng 250k, mở đợt thu giúp",
      "moi phu huynh dong 250k mo dot thu giup",
      "tạo lần thu 80k đều nhau",
      "tao lan thu 80k deu nhau",
      "thu tiền hội đồng hương 300k mỗi người",
      "thu tien hoi dong huong 300k moi nguoi",
      "mở thu quỹ",
      "mo thu quy",
    ],
    // 🔴 US-AIV2-0111 C06 (@UC0320) — câu HỎI (việc XEM) không thuộc trang ghi này; mỗi câu là ví dụ của một trang đọc ⇒ eval đo được.
    questionsNotHere: ["đợt thu nào đang mở?"],
  },
  /**
   * 🔴 US-AIV2-0110 S03 (@UC0260 · QĐ PO 4) — ĐÁP ÁN TIỀN của bộ eval, VIẾT TAY từ chính câu (không từ đầu ra model).
   * KHÔNG vào prompt ⇒ không đổi hash theo ca. Câu vắng mặt ở đây: không nói số tiền.
   * Câu #03 "hai trăm nghìn" (chữ viết số) CÓ đáp án: người đọc thấy rõ 200.000. Từ US-AIV2-0110 S18 (@UC0262) bộ đổi tiền
   * ĐỌC được dạng chữ này (trước đó ca này trượt chiều amount vì bộ đổi tiền chỉ nhận chữ số).
   */
  evalAmounts: {
    "mở đợt thu 200k mỗi người": "200000",
    "mo dot thu 200k moi nguoi": "200000",
    "thu quỹ lớp hai trăm nghìn một bạn": "200000",
    "thu quy lop 200 nghin mot ban": "200000",
    "tạo đợt thu đều 500k": "500000",
    "tao dot thu deu 500k": "500000",
    "thu mỗi người 1 triệu tiền quỹ đầu năm": "1000000",
    "thu 300k/người": "300000",
    "thu tiền quỹ lần này 150k": "150000",
    "mọi người đóng 100k nhé": "100000",
    "thu 200k": "200000",
    "thu tien quy lan nay 150k": "150000",
    "moi nguoi dong 100k nhe": "100000",
    "thu 300k moi nguoi": "300000",
    "mở lần thu mới, mỗi người 250k": "250000",
    "mo lan thu moi moi nguoi 250k": "250000",
    "tạo đợt thu quỹ lớp 400k một suất": "400000",
    "tao dot thu quy lop 400k mot suat": "400000",
    "cả nhóm đóng đều 50k": "50000",
    "ca nhom dong deu 50k": "50000",
    "thu tiền đội bóng mỗi bạn 120k": "120000",
    "thu tien doi bong moi ban 120k": "120000",
    "cho mình mở đợt thu 1 triệu mỗi người": "1000000",
    "cho minh mo dot thu 1 trieu moi nguoi": "1000000",
    "thu quỹ đầu năm 700k một người": "700000",
    "thu quy dau nam 700k mot nguoi": "700000",
    "mỗi phụ huynh đóng 250k, mở đợt thu giúp": "250000",
    "moi phu huynh dong 250k mo dot thu giup": "250000",
    "tạo lần thu 80k đều nhau": "80000",
    "tao lan thu 80k deu nhau": "80000",
    "thu tiền hội đồng hương 300k mỗi người": "300000",
    "thu tien hoi dong huong 300k moi nguoi": "300000",
  },
  executor: "adhocCollection.createAdhoc",
  // review #832 MED-1: `createAdhoc` gặp lại khoá idempotency ⇒ REPLAY, 0 dòng mới ⇒ trang PHẢI có câu `doneAlready` (validator ép).
  executorMayNoop: true,
  /** AI-COL-OPEN-PHRASING (@UC0195 @UC0220): lưới mở đợt thu theo đầu người — kiểu đợt + ô nhận chữ của câu (`collection-open.ts`). Không vào prompt ⇒ 0 ca eval đổi hash. */
  collectionFromSentence: { cadence: "once", amount: "soTien", name: "tenDot" },
  /** `@UC0216` — cửa mobile trang này chịu trách nhiệm: cửa mở đợt thu; `COL-DEADLINE` là BIẾN THỂ cùng cửa nên khai rỗng — mỗi cửa một chủ. */
  endpoints: [
    "POST /funds/{fundId}/collections",
  ],
  facts: ["soNguoi", "tongTien", "tenDotGhi", "hanGhi", "tenDotThat", "hanThat"],
  templates: {
    /**
     * 🟠 **NỢ CÓ ĐỊA CHỈ — review #884 M-1, chờ PO: `sdlc_opt/01-backlog/po-decisions-pending.md` §A-SEXIES.**
     *
     * §A-QUINQUIES nêu nguyên nhân gồm HAI nửa: chuỗi mô tả đứng *"trong **ngoặc kép**, ở dòng nhãn `"Tên đợt
     * thu"`"*. PO ký phương án 2 = đổi **nhãn** (đã vá, xem `labels.tenDotGhi`). Cặp `“ ”` quanh `{tenDotGhi}`
     * dưới đây là **nửa còn lại** — nó đánh dấu danh xưng y như nhãn cũ, chỉ ở bề mặt khác (`turn.text` thay
     * vì `card.lines`), và ca khoá hiện chỉ soi `card.lines` nên bề mặt chưa vá cũng là bề mặt chưa đo.
     *
     * 🔴 **KHÔNG tự bỏ ngoặc kép**: đó là đổi văn xuôi thẻ xác nhận T2 của một trang TIỀN mà PO vừa đích thân
     * phân xử cùng ngày, và PO chọn *"sửa cái NHÃN, không phải chuỗi"*. Suy từ lý lẽ PO ra một thay đổi PO
     * không chọn = đúng lớp lỗi §A7 (*"lời một agent không phải chữ ký PO"*). Bỏ ngoặc kép cần QĐ PO MỚI, và
     * khi có thì làm CẢ HAI nửa một lượt (bỏ ngoặc + mở phép đo sang `turn.text`), đừng lại vá một nửa.
     *
     * ⚠️ Ngoặc kép ở `done`/`doneAlready` **đúng và giữ nguyên**: chúng bọc `{tenDotThat}` — một cái TÊN THẬT.
     * Luật: *ngoặc kép đánh dấu một cái TÊN (`*That`), không đánh dấu một LỜI HỨA (`*Ghi`)*.
     */
    confirm: "Mở đợt thu “{tenDotGhi}” ở nhóm {nhom}, quỹ {quy}: mỗi người {soTien}, {soNguoi} người, tổng {tongTien}, hạn {hanGhi}?",
    // 🔴 US-AIV2-0080: câu này chạy SAU khi đã ghi ⇒ nói tên + hạn THẬT (`*That`), không nói lại lời hứa không-cụ-thể.
    done: "Đã mở đợt thu “{tenDotThat}” — {soNguoi} người × {soTien} = {tongTien}, hạn {hanThat}. Chưa ai đóng; muốn nhắc thì nói “nhắc mọi người đóng”.",
    /**
     * 🔴 Lượt KHÔNG ghi gì (review #832 HIGH-1). Ba việc câu này phải làm, theo đúng thứ tự người đọc cần:
     *  1. nói thẳng là **không tạo thêm** (không được để người dùng đoán từ chữ "đã");
     *  2. chỉ ra đợt ĐANG có, để họ biết mình đang nhìn cái gì;
     *  3. **mở cửa thoát bằng câu gõ được** — ô `tenDot` không bao giờ được hỏi (ô tuỳ chọn, không `autofill`), nên nếu câu này
     *     không dạy cách đặt tên thì cửa thoát coi như đóng. Ví dụ trong câu là thứ người dùng gõ lại được nguyên văn.
     * CỐ Ý không nhắc "Chưa ai đóng": đợt gốc có thể đã có người đóng — máy không được phát biểu một mệnh đề về tiền mà nó không đo.
     */
    doneAlready:
      "Đợt thu “{tenDotThat}” ({soTien} mỗi người, hạn {hanThat}) đã có trong quỹ {quy} rồi, nên mình KHÔNG tạo thêm. Muốn mở một đợt nữa thì đặt tên khác cho nó, ví dụ: “mở đợt thu {soTien} mỗi người, tên là Dã ngoại”.",
  },
  /** P5.3 — nhãn NGƯỜI ĐỌC cho từng dòng phiếu T2; thiếu một nhãn thì `renderConfirm` ném (không camelCase lên màn tiền). */
  labels: {
    /**
     * 🔴 **NHÃN MÔ TẢ, KHÔNG PHẢI DANH XƯNG** — QĐ PO 20/09/2026 §A-QUINQUIES (phương án 2), từ review #884 HIGH-2a.
     *
     * Nhãn cũ `"Tên đợt thu"` **hứa một danh xưng**. Khi người dùng không đặt tên, giá trị của dòng là
     * `COL_EQUAL_TEN_MAC_DINH` = "Thu quỹ tháng bạn bấm Đồng ý" — một **câu mô tả cách máy sẽ đặt tên**, không phải
     * một cái tên. Đặt câu mô tả đó dưới nhãn danh xưng (lại còn trong ngoặc kép ở `templates.confirm`) ⇒ người đọc
     * hiểu đó LÀ tên, rồi lượt báo xong gọi ra **một tên khác** (`{tenDotThat}` = "Thu quỹ tháng 9/2026").
     *
     * PO chọn sửa **đúng chỗ sai**: cái NHÃN, không phải chuỗi. `COL_EQUAL_TEN_MAC_DINH` **giữ nguyên**, cách đặt tên
     * của trang đang chạy **không đổi một chữ** (phương án "tên mặc định thành hằng" bị loại vì danh sách đợt thu các
     * tháng sẽ trùng tên). Nhãn mới đứng cùng register với `hanGhi: "Hạn đóng"` — một **thuộc tính** mà giá trị được
     * phép là câu mô tả — và vẫn đọc xuôi khi người dùng CÓ đặt tên ("Đợt thu sẽ mở: Dã ngoại").
     *
     * Điều kiện đóng đo được (§A-QUINQUIES): nhãn **không chứa** từ "tên". Ca khoá ở `col-equal.page.test.ts` neo vào
     * **tính chất** đó chứ không đọc lại hằng — xem `describe` "nhãn dòng tên đợt là MÔ TẢ".
     */
    tenDotGhi: "Đợt thu sẽ mở",
    nhom: "Nhóm",
    quy: "Quỹ",
    soTien: "Mỗi người",
    soNguoi: "Số người",
    tongTien: "Tổng thu",
    hanGhi: "Hạn đóng",
  },
  journey: { outcomes: { done: { next: ["DEBT-REMIND", "READ-COLLECTIONS"] }, done_already: { next: ["READ-COLLECTIONS", "DEBT-REMIND"] } } },
  /** UX-OPEN-TASK-EDIT (@UC0165): lời sửa gắn trang anh em (cùng hành động MỞ ĐỢT THU, khác kiểu hạn) áp được vào phiếu đang mở của trang này. */
  editPeers: ["COL-DEADLINE"],
  help: { title: "Mở đợt thu đều", tryNow: "mở đợt thu 200k mỗi người" },
} satisfies AiCapabilityPage;
